import { Trans, useTranslation } from 'react-i18next';
import { Stack, styled, SvgIcon, Typography } from '@mui/material';

import type { MarketingOrgInfo, SumPlanState } from '@/api/domains/dorim-promo.types';
import { ReactComponent as DoneSVG } from '@/shared/assets/icons-2.0/done.svg';

const getDescription = (planState: SumPlanState) => {
  switch (planState) {
    case 'underMinPlan':
      return (
        <Trans
          ns="specifications-detail-page"
          i18nKey="marketing-org-card.marketing-org-card-info-block.description.underMinPlan"
        />
      );
    case 'minPlanCompletion':
      return (
        <Trans
          ns="specifications-detail-page"
          i18nKey="marketing-org-card.marketing-org-card-info-block.description.minPlanCompletion"
        />
      );
    case 'maxPlanCompletion':
      return (
        <Trans
          ns="specifications-detail-page"
          i18nKey="marketing-org-card.marketing-org-card-info-block.description.maxPlanCompletion"
        />
      );
    default:
      return (
        <Trans
          ns="specifications-detail-page"
          i18nKey="marketing-org-card.marketing-org-card-info-block.description.default"
        />
      );
  }
};

const StyledRoot = styled(Stack)({
  width: 360,
  justifyContent: 'center',
}) as typeof Stack;

type MarketingOrgCardInfoBlockProps = {
  hasSubtitle?: boolean;
  hasDescription?: boolean;
  marketingOrgName?: MarketingOrgInfo['full_name'];
  sumPlanState: SumPlanState;
};

export const MarketingOrgCardInfoBlock = (props: MarketingOrgCardInfoBlockProps) => {
  const { hasSubtitle = true, hasDescription = true, marketingOrgName = '-', sumPlanState } = props;
  const { t } = useTranslation('specifications-detail-page');

  const renderName = () => {
    const nameElement = (
      <Stack spacing={1} alignItems="center" direction="row">
        <Typography variant="Title/Bold/large" sx={{ maxWidth: 300 }}>
          {marketingOrgName}
        </Typography>
        {sumPlanState === 'maxPlanCompletion' && <SvgIcon component={DoneSVG} sx={{ color: 'custom.green' }} />}
      </Stack>
    );

    if (hasSubtitle) {
      return (
        <div>
          {nameElement}
          <Typography variant="Body/medium" color="neutral.700">
            {t('marketing-org-card.marketing-org-card-info-block.subtitle')}
          </Typography>
        </div>
      );
    }

    return nameElement;
  };

  return (
    <StyledRoot spacing={2}>
      {renderName()}
      {hasDescription && <Typography variant="Body/medium">{getDescription(sumPlanState)}</Typography>}
    </StyledRoot>
  );
};
